import cn from 'classnames'
import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import AlertCircleIcon from 'ext-ui/svg/icon/AlertCircleIcon'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import React, { ReactElement, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { AssignCardFeedCardsSuccessToastMessage } from 'src/app/Toast/Toast'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { OrganizationRoles } from 'src/enums'
import EditAccessAcknowledgement from 'src/feature/EditAccessAcknowledgement/EditAccessAcknowledgement'
import { useTranslate } from 'src/localization/Trns'
import { supportRequestUrl } from 'src/navigation/urls'
import { CardFeedCard } from 'src/queries/cardFeeds/cardFeeds.models'
import { useAssignCardFeedCards } from 'src/queries/cardFeeds/domain/cardFeeds.queries'

import AssignmentCardList from './components/AssignmentCardList'
import RecipientHeader from './components/RecipientHeader'
import style from './ConfirmCardAssignmentModal.style'

/** The user the selected cards are being assigned to. */
export interface AssignmentRecipient {
  email: string
  name?: string | null
  avatarUrl?: string | null
  /**
   * The recipient's current org role. `null`/`undefined` means a brand-new
   * email that isn't in the org yet (they'll be invited).
   */
  currentRole?: OrganizationRoles | null
}

/**
 * Drives the three modal designs:
 * - `newInvite`: email is not yet an org member.
 * - `promote`: existing Member / Bookkeeper gaining Card Manager access.
 * - `elevated`: Admin / Owner / Card Manager getting additional card access.
 */
export type AssignmentVariant = 'newInvite' | 'promote' | 'elevated'

export const getAssignmentVariant = (
  role: OrganizationRoles | null | undefined,
): AssignmentVariant => {
  if (!role) return 'newInvite'
  if (
    role === OrganizationRoles.admin ||
    role === OrganizationRoles.owner ||
    role === OrganizationRoles.cardManager
  ) {
    return 'elevated'
  }
  return 'promote'
}

export type ConfirmCardAssignmentModalProps = BaseModalProps & {
  recipient: AssignmentRecipient
  cards: CardFeedCard[]
  cardFeedId: string
  /** Called with the number of cards assigned after a successful request. */
  onAssigned?: (assignedCount: number) => void
}

const ConfirmCardAssignmentModal = (
  props: ConfirmCardAssignmentModalProps,
): ReactElement => {
  const {
    className = '',
    isOpen,
    onClose,
    recipient,
    cards,
    cardFeedId,
    onAssigned,
  } = props

  const translate = useTranslate()
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()
  const [acknowledged, setAcknowledged] = useState(false)
  const assignCards = useAssignCardFeedCards()

  const variant = getAssignmentVariant(recipient.currentRole)

  // Fires when the modal becomes visible — this is the card-feed access
  // acknowledgement surface (the People-flow role-change popup is a separate
  // surface and tracks its own events).
  useEffect(() => {
    if (isOpen) {
      analytics.track('card feed details', { action: 'role change ack viewed' })
    }
  }, [isOpen, analytics])

  const handleClose = (): void => {
    onClose()
    setAcknowledged(false)
    assignCards.reset()
  }

  // User-initiated dismissal (Cancel button / backdrop / X). Kept separate from
  // handleClose so the post-confirm cleanup doesn't emit a cancel event.
  const handleCancel = (): void => {
    analytics.track('card feed details', { action: 'role change ack canceled' })
    handleClose()
  }

  const handleAcknowledgeChange = (checked: boolean): void => {
    analytics.track('card feed details', {
      action: 'role change toggled',
      acknowledged: checked,
    })
    setAcknowledged(checked)
  }

  const handleConfirm = async (): Promise<void> => {
    analytics.track('card feed details - access confirmed')
    analytics.track('confirm card assignment modal', { variant })
    try {
      const result = await assignCards.mutateAsync({
        cardFeedId,
        body: {
          email: recipient.email,
          creditCardIds: cards.map((card) => card.id),
        },
      })
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <AssignCardFeedCardsSuccessToastMessage
              assignedCount={result.assignedCount}
            />
          ),
        }),
      )
      onAssigned?.(result.assignedCount)
      handleClose()
    } catch {
      // Error surfaced via the inline AlertBanner below (assignCards.isError);
      // keep the modal open so the user can retry without re-entering anything.
    }
  }

  const role = <span css={style.roleEmphasis}>{translate('Card Manager')}</span>

  const subtitle =
    variant === 'elevated'
      ? translate(
          'Confirm the edit access this user will have for the selected card accounts.',
        )
      : translate(
          "Please confirm you'd like to assign the following card accounts to this user and grant them ${role} access.",
          { role },
        )

  const errorMessage = assignCards.isError
    ? (assignCards.error?.response?.data?.error ??
      translate(
        'Something went wrong assigning these cards. Please try again.',
      ))
    : undefined

  return (
    <BaseModal
      className={cn('ConfirmCardAssignmentModal', className)}
      variant="popup"
      isOpen={isOpen}
      onClose={handleCancel}
      css={style.modal}
      aria-label={translate('Confirm Card Assignment & Access')}
    >
      <h2 css={style.title}>{translate('Confirm Card Assignment & Access')}</h2>
      <p css={style.subtitle}>
        {subtitle}
        {variant === 'newInvite' && (
          <>
            {' '}
            {translate("They'll receive an invite to set up their account.")}
          </>
        )}
      </p>

      <section css={style.summaryCard}>
        <RecipientHeader
          variant={variant}
          email={recipient.email}
          name={recipient.name}
          avatarUrl={recipient.avatarUrl}
          currentRole={recipient.currentRole}
        />
        <div css={style.divider} />
        <AssignmentCardList cards={cards} />
      </section>

      <section css={style.acknowledgeCard}>
        <EditAccessAcknowledgement
          checked={acknowledged}
          onChange={handleAcknowledgeChange}
        />
      </section>

      <AlertBanner
        variant="warning"
        icon={<AlertCircleIcon css={style.warningIcon} />}
        title={translate(
          "Cards can't be reassigned once the assignment is active",
        )}
        data-testid="confirm-card-assignment-reassign-warning"
      >
        <span>
          {translate(
            "You can cancel and reassign while the assignment is still pending. Once it's active, reach out to ${supportLink} if you need to make any changes.",
            {
              supportLink: (
                <HoverableLink
                  href={supportRequestUrl()}
                  ariaLabelSource={'Contact customer support'}
                  testId={'confirm-card-assignment-support-link'}
                  regular
                  underline
                >
                  {translate('customer support')}
                </HoverableLink>
              ),
            },
          )}
        </span>
      </AlertBanner>

      {errorMessage && (
        <AlertBanner
          variant="severe"
          icon={<AlertTriangleIcon />}
          title={translate('There was an error assigning these cards')}
          data-testid="confirm-card-assignment-error"
        >
          <span>{errorMessage}</span>
        </AlertBanner>
      )}

      <div css={style.footer}>
        <Button
          variant="no-shell"
          css={style.cancelButton}
          onClick={handleCancel}
          data-testid="confirm-card-assignment-cancel"
        >
          {translate('Cancel')}
        </Button>
        <Button
          variant="primary"
          loading={assignCards.isPending}
          disabled={
            !acknowledged || assignCards.isPending || cards.length === 0
          }
          onClick={handleConfirm}
          data-testid="confirm-card-assignment-confirm"
        >
          {translate('Confirm & Assign')}
        </Button>
      </div>
    </BaseModal>
  )
}

export default ConfirmCardAssignmentModal
